import { ChevronDown, ChevronTop } from 'assets/icons';
import BadgeSquare from 'components/badge/badgeSquare';
import {
  fillText,
  listImportLabel,
} from 'components/invoiceImport/invoiceImportText';
import {
  countOf,
  formatCount,
  formatDuration,
  formatRunStart,
  runOutcome,
} from './journalRunText';

const StepErrors = ({ text, failed }) =>
  failed > 0 ? (
    <BadgeSquare
      type={3}
      size="xs"
      customLabel={fillText(text('history.inError', '{count} en erreur'), {
        count: formatCount(failed),
      })}
    />
  ) : null;

/**
 * One run of the "Historique" tab (JBWEB-1281, mockup 2.9): when, what, by
 * whom and how long, its counters, and unfolded, each step and a link to the
 * invoices it left to fix.
 */
const JournalRunBlock = ({ run, text, isOpen, onToggle, onShowToFix }) => {
  const { bestt, pennylane } = run.counters;
  const outcome = runOutcome(text, run);

  const author = [run.startedBy?.firstname, run.startedBy?.lastname]
    .filter(Boolean)
    .join(' ');
  let duration = null;
  if (run.status === 'running') {
    duration = text('history.inProgress', 'en cours');
  } else if (typeof run.durationMs === 'number') {
    duration = formatDuration(text, run.durationMs);
  }
  // An import of a list names its count (JBWEB-1274); a retry has no scope.
  const label =
    run.type === 'retry'
      ? text('history.retry', 'Réessai')
      : (listImportLabel(text, run, 'history.') ??
        text('history.import', 'Import'));
  const line = [
    label,
    author && fillText(text('history.by', 'par {name}'), { name: author }),
    duration,
  ]
    .filter(Boolean)
    .join(' · ');

  const summary = [
    countOf(text, 'fetched', ['1 récupérée', '{count} récupérées'], bestt.done),
    countOf(
      text,
      'imported',
      ['1 importée', '{count} importées'],
      pennylane.done,
    ),
  ].join(' · ');

  return (
    <section className="rounded-lg border border-gray-200">
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900">
            {formatRunStart(text, run.startedAt)}
          </h3>
          <div className="text-sm text-gray-500">{line}</div>
          <div className="text-xs text-gray-500">{summary}</div>
          {outcome.sentence && (
            <div className="text-xs text-gray-700">{outcome.sentence}</div>
          )}
        </div>
        <BadgeSquare
          type={outcome.type}
          size="xs"
          customLabel={outcome.label}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={
            isOpen ? text('hideGroup', 'Replier') : text('showGroup', 'Déplier')
          }
          className="shrink-0 flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-50"
        >
          {isOpen ? (
            <ChevronTop wh={16} color="#374151" />
          ) : (
            <ChevronDown wh={16} color="#374151" />
          )}
        </button>
      </div>
      {isOpen && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          <div className="flex flex-col gap-3 rounded-md bg-gray-100 px-3 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {text('history.stepBestt', '1 · Récupération depuis Bestt')}
                </div>
                <div className="text-sm text-gray-500">
                  {countOf(
                    text,
                    'stepFetched',
                    ['1 facture récupérée', '{count} factures récupérées'],
                    bestt.done,
                  )}
                </div>
              </div>
              <StepErrors text={text} failed={bestt.failed} />
            </div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {text('history.stepPennylane', '2 · Import dans Pennylane')}
                </div>
                <div className="text-sm text-gray-500">
                  {countOf(
                    text,
                    'stepImported',
                    ['1 facture importée', '{count} factures importées'],
                    pennylane.done,
                  )}
                </div>
              </div>
              <StepErrors text={text} failed={pennylane.failed} />
            </div>
          </div>
          {run.openErrors > 0 && (
            <button
              type="button"
              onClick={onShowToFix}
              className="self-start text-sm font-medium text-gray-900 underline"
            >
              {countOf(
                text,
                'toFix',
                [
                  '1 facture à corriger → voir l’onglet À corriger',
                  '{count} factures à corriger → voir l’onglet À corriger',
                ],
                run.openErrors,
              )}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default JournalRunBlock;
